import { cn } from '@dialogueconsulting/sked-ui/lib/utils'

import {
  CarouselIcon,
  ReelIcon,
  StoryIcon,
  TextIcon,
  VideoIcon,
  TilePendingIcon,
  TileApprovedIcon,
  TileRejectedIcon,
  VideoPlayIcon,
  NotificationIcon,
} from 'Icons'
import type { PostType, Platform } from 'Types'

import ImageWithFallback from './Common/ImageWithFallback'
import PlatformIcon from './Common/PlatformIcon'

// SKE-3795 — post card aligned to the Figma calendar "CalendarPostCard"
// (node 3591:56000): a flat white card with a status-coloured left bar, a
// full-height thumbnail (with the dark media-type badge mirrored from
// UpcomingPostCard), the time + status icon on the right, and — in the
// agenda variant — a one-line caption. Status colours follow StatusLabel
// (amber = pending / green = approved / red = requestChange).
const STATUS_BAR_COLOR: Record<string, string> = {
  pending: 'var(--color-yellow-500)',
  approved: 'var(--color-green-500)',
  requestChange: 'var(--color-red-500)',
}

// Media-type glyph shown in the dark badge overlaid on the thumbnail. The
// media/* icons are white-fillable (unlike the calendar/tile/* set), so they
// read correctly on bg-black/40 — the same pattern UpcomingPostCard uses.
const renderMediaTypeIcon = (type: PostType) => {
  switch (type) {
    case 'carousel':
      return <CarouselIcon data-testid="carousel_icon" />
    case 'video':
      return <VideoIcon data-testid="video_icon" />
    case 'reel':
      return <ReelIcon data-testid="reel_icon" />
    case 'story':
      return <StoryIcon data-testid="story_icon" />
    case 'text':
      return <TextIcon data-testid="text_icon" />
    case 'singleImage':
      return null
  }
}

export interface CalendarTileProps extends React.ComponentPropsWithoutRef<'div'> {
  platform: Platform
  status: string
  mediaSrc: string
  time: string
  type: PostType
  caption: string
  hasNotification: boolean
  isReadOnly: boolean
  /** Agenda pane variant — renders a one-line caption under the meta row. */
  showCaption?: boolean
}

const CalendarTile: React.FC<CalendarTileProps> = ({
  platform,
  status,
  mediaSrc,
  time,
  type,
  caption,
  hasNotification,
  isReadOnly,
  showCaption = false,
  onClick,
  onKeyDown,
  className,
  ...props
}) => {
  const showVideoPlayIcon =
    type === 'video' || type === 'reel' || type === 'story'
  const mediaTypeIcon = renderMediaTypeIcon(type)
  const barColor = !isReadOnly ? STATUS_BAR_COLOR[status] : undefined
  // Interactive only when a click handler is supplied (calendar + agenda
  // tiles); stays a plain div otherwise so non-clickable uses aren't announced
  // as buttons. Enter/Space mirror the click for keyboard users.
  const isInteractive = typeof onClick === 'function'

  return (
    <div
      className={cn(
        'relative flex min-h-[40px] items-stretch overflow-hidden rounded-lg bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition-shadow duration-150 hover:shadow-[2px_2px_9px_0_rgba(0,0,0,0.15)]',
        isInteractive && 'cursor-pointer',
        className
      )}
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={(event) => {
        if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          event.currentTarget.click()
        }
        onKeyDown?.(event)
      }}
      {...props}
    >
      {/* Status accent bar (hidden in read-only — no actionable status) */}
      {barColor && (
        <span
          aria-hidden="true"
          className="w-1 shrink-0 self-stretch"
          style={{ background: barColor }}
        />
      )}

      {/* Thumbnail — full card height */}
      <div className="relative w-10 shrink-0 self-stretch overflow-hidden bg-neutral-50">
        {type !== 'text' ? (
          <ImageWithFallback
            variant="media"
            src={mediaSrc}
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 overflow-hidden p-1 text-left text-[6px] leading-[8px] text-neutral-700">
            <div className="line-clamp-5">{caption}</div>
          </div>
        )}

        {showVideoPlayIcon && (
          <VideoPlayIcon
            className="absolute left-1/2 top-1/2 w-5 -translate-x-1/2 -translate-y-1/2"
            data-testid="videoplay_icon"
          />
        )}

        {/* Dark media-type badge (matches the Figma "_Media type" overlay).
            media/* glyphs are fill-based and tint white on the dark pill — the
            same treatment UpcomingPostCard uses. text keeps its own placeholder
            artwork, so it isn't force-tinted. */}
        {mediaTypeIcon && (
          <span
            className={cn(
              'absolute left-0.5 top-0.5 grid place-items-center rounded-full bg-black/40 p-[3px] [&_svg]:size-2.5',
              type !== 'text' && '[&_svg]:fill-white'
            )}
          >
            {mediaTypeIcon}
          </span>
        )}

        {/* Platform glyph — kept (the Figma grid card omits it; external review
            benefits from it). Overlaid bottom-right so it doesn't consume the
            narrow meta row. */}
        <span className="absolute bottom-0.5 right-0.5 grid size-4 place-items-center rounded-full bg-white shadow-sm [&_svg]:size-2.5">
          <PlatformIcon platform={platform} />
        </span>
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-1.5 py-1">
        <div className="flex items-center gap-1">
          <span className="truncate text-[11px] font-medium leading-4 text-neutral-600">
            {time}
          </span>
          {hasNotification && (
            <span className="grid shrink-0 place-items-center [&_svg]:size-3">
              <NotificationIcon data-testid="notification_icon" />
            </span>
          )}
          {!isReadOnly && (
            <span
              data-testid="status-icon"
              className="ml-auto grid shrink-0 place-items-center [&_svg]:size-3.5"
            >
              {status === 'pending' && (
                <TilePendingIcon data-testid="pending_icon" />
              )}
              {status === 'approved' && (
                <TileApprovedIcon data-testid="approved_icon" />
              )}
              {status === 'requestChange' && (
                <TileRejectedIcon data-testid="rejected_icon" />
              )}
            </span>
          )}
        </div>

        {showCaption && caption && (
          <p
            className={cn(
              'm-0 truncate text-xs font-semibold leading-4 text-neutral-800',
              type === 'text' && 'font-medium'
            )}
          >
            {caption}
          </p>
        )}
      </div>
    </div>
  )
}

export default CalendarTile
